import { MessageCircle, Mail } from "lucide-react";
import {
  SALES_EMAIL,
  WHATSAPP_NUMBER,
  RESERVE_CTA_LABEL,
  RESERVE_MICROCOPY,
} from "@/lib/socialLinks";

interface OrderOfflineButtonProps {
  productName: string;
  productSku?: string | null;
  productUrl?: string;
  variant?: "full" | "compact";
}

const buildMessage = (productName: string, sku?: string | null, url?: string) => {
  const link = url ?? (typeof window !== "undefined" ? window.location.href : "");
  const skuLine = sku ? ` (SKU: ${sku})` : "";
  return `Hi Plymhair, I'd like to reserve "${productName}"${skuLine} ahead of the next restock. Please let me know expected availability and how to secure it.${link ? `\n\nLink: ${link}` : ""}`;
};

const OrderOfflineButton = ({ productName, productSku, productUrl, variant = "full" }: OrderOfflineButtonProps) => {
  const message = buildMessage(productName, productSku, productUrl);
  const subject = `Reservation request – ${productName}`;
  const waHref = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`;
  const mailHref = `mailto:${SALES_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(message)}`;

  if (variant === "compact") {
    return (
      <div className="flex flex-col items-center gap-1.5">
        <span className="font-display text-[11px] tracking-wide text-foreground/80 italic">
          {RESERVE_CTA_LABEL}
        </span>
        <div className="flex gap-2.5">
          <a
            href={waHref}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Reserve ${productName} via WhatsApp`}
            onClick={(e) => e.stopPropagation()}
            className="w-9 h-9 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow-md hover:bg-primary/90 hover:scale-110 active:scale-95 transition-all duration-200"
          >
            <MessageCircle className="w-4 h-4" />
          </a>
          <a
            href={mailHref}
            aria-label={`Reserve ${productName} via email`}
            onClick={(e) => e.stopPropagation()}
            className="w-9 h-9 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow-md hover:bg-primary/90 hover:scale-110 active:scale-95 transition-all duration-200"
          >
            <Mail className="w-4 h-4" />
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center gap-3">
      <span className="font-display text-[13px] tracking-wide text-foreground/80 italic">
        {RESERVE_CTA_LABEL}
      </span>
      <div className="flex gap-4">
        <a
          href={waHref}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`Reserve ${productName} via WhatsApp`}
          className="w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow-lg hover:bg-primary/90 hover:scale-110 active:scale-95 transition-all duration-300"
        >
          <MessageCircle className="w-5 h-5" />
        </a>
        <a
          href={mailHref}
          aria-label={`Reserve ${productName} via email`}
          className="w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow-lg hover:bg-primary/90 hover:scale-110 active:scale-95 transition-all duration-300"
        >
          <Mail className="w-5 h-5" />
        </a>
      </div>
      <p className="text-[11px] text-muted-foreground font-body text-center italic">
        {RESERVE_MICROCOPY}
      </p>
    </div>
  );
};

export default OrderOfflineButton;
